import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Highlight {
  icon: string;
  title: string;
  desc: string;
}

interface Faq {
  q: string;
  a: string;
}

interface Specs {
  material: string;
  size: string;
  weight: string;
  moq?: number;
}

interface ProductInfoProps {
  name: string;
  productCode?: string;
  category: string;
  price: number;
  originalPrice: number;
  tags: string[];
  description: string;
  longDescription: string;
  specs: Specs;
  highlights: Highlight[];
  faqs: Faq[];
}

type TabKey = 'desc' | 'specs' | 'faq';

const TAG_I18N_MAP: Record<string, string> = {
  '热销中': 'tag_hot_selling',
  '新品': 'tag_new',
  '瓦楞纸': 'tag_corrugated_paper',
  '限时特惠': 'tag_limited_offer',
  '爆款': 'tag_bestseller',
  '推荐': 'tag_recommended',
  '猫薄荷': 'tag_catnip',
  '剑麻': 'tag_sisal',
  '护角': 'tag_corner_protector',
  '爬架': 'tag_cat_tree',
  '环保': 'tag_eco_friendly',
  '热销': 'tag_hot_selling',
  '双面可用': 'tag_double_sided',
  '护沙发': 'tag_sofa_protector',
  '多功能': 'tag_multi_function',
};

const CATEGORY_I18N_MAP: Record<string, string> = {
  '瓦楞纸': 'category_corrugated',
  '剑麻': 'category_sisal',
  '综合': 'category_combo',
};

const HIGHLIGHT_TITLE_I18N_MAP: Record<string, string> = {
  '三层热压工艺': 'highlight_title_triple_layer',
  '有机猫薄荷注入': 'highlight_title_catnip_infused',
  '环保无毒认证': 'highlight_title_eco_certified',
  '防滑底部设计': 'highlight_title_anti_slip',
  '天然剑麻绳': 'highlight_title_natural_sisal',
  '实木底座': 'highlight_title_solid_wood',
  '60cm黄金高度': 'highlight_title_golden_height',
  '自然修爪': 'highlight_title_nail_care',
  '双面可用': 'highlight_title_double_sided',
  '波浪曲面设计': 'highlight_title_wave_curve',
  '低粉尘配方': 'highlight_title_low_dust',
  '防滑橡胶条': 'highlight_title_rubber_strip',
  '专为沙发设计': 'highlight_title_sofa_design',
  '免工具安装': 'highlight_title_tool_free',
  '不损伤沙发': 'highlight_title_sofa_safe',
  '一套4个': 'highlight_title_set_of_4',
  '有机猫薄荷内嵌': 'highlight_title_catnip_builtin',
  '圆形美观设计': 'highlight_title_round_design',
  '边缘安全处理': 'highlight_title_safe_edge',
  '热销爆款': 'highlight_title_hot_seller',
  '三层平台设计': 'highlight_title_three_level',
  '承重15kg+': 'highlight_title_15kg_capacity',
  '悬挂玩具配件': 'highlight_title_hanging_toy',
  '宽大稳固底盘': 'highlight_title_wide_base',
};

const HIGHLIGHT_DESC_I18N_MAP: Record<string, string> = {
  '密度是普通产品2倍，更耐抓': 'highlight_desc_triple_layer',
  '持久香气，猫咪主动靠近': 'highlight_desc_catnip_infused',
  '啃咬也安全放心': 'highlight_desc_eco_certified',
  '手工缠绕，粗细均匀韧性足': 'highlight_desc_natural_sisal',
  '松木防腐处理，稳固不倒': 'highlight_desc_solid_wood',
  '满足猫咪站立伸展需求': 'highlight_desc_golden_height',
  '有效修剪猫爪，保护家具': 'highlight_desc_nail_care',
  '翻转继续使用，寿命翻倍': 'highlight_desc_double_sided',
  '契合猫咪自然抓挠姿势': 'highlight_desc_wave_curve',
  '纸屑少，保持家居整洁': 'highlight_desc_low_dust',
  '完美贴合沙发角，全方位保护': 'highlight_desc_sofa_design',
  '强力魔术贴，30秒搞定': 'highlight_desc_tool_free',
  '背面保护层，不留痕迹': 'highlight_desc_sofa_safe',
  '同时保护沙发四个角': 'highlight_desc_set_of_4',
  '解决猫咪不爱用抓板难题': 'highlight_desc_catnip_builtin',
  '可平放可挂墙，一物两用': 'highlight_desc_round_design',
  '无毛刺，猫咪使用安全': 'highlight_desc_safe_edge',
  '月销超5000件，好评如潮': 'highlight_desc_hot_seller',
  '满足猫咪喜欢高处的天性': 'highlight_desc_three_level',
  '多猫家庭也能放心使用': 'highlight_desc_15kg_capacity',
  '激发猫咪玩耍兴趣': 'highlight_desc_hanging_toy',
  '跳跃也不倾倒': 'highlight_desc_wide_base',
};

export default function ProductInfo({
  name,
  productCode,
  category,
  price,
  originalPrice,
  tags,
  description,
  longDescription,
  specs,
  highlights,
  faqs,
}: ProductInfoProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>('desc');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const discount = Math.round((1 - price / originalPrice) * 100);

  const tabs: { key: TabKey; labelKey: string }[] = [
    { key: 'desc', labelKey: 'detail_tab_desc' },
    { key: 'specs', labelKey: 'detail_tab_specs' },
    { key: 'faq', labelKey: 'detail_tab_faq' },
  ];

  const translateTag = (tag: string): string => {
    const key = TAG_I18N_MAP[tag];
    return key ? t(key) : tag;
  };

  const translateCategory = (cat: string): string => {
    const key = CATEGORY_I18N_MAP[cat];
    return key ? t(key) : cat;
  };

  const translateHighlightTitle = (title: string): string => {
    const key = HIGHLIGHT_TITLE_I18N_MAP[title];
    return key ? t(key) : title;
  };

  const translateHighlightDesc = (desc: string): string => {
    const key = HIGHLIGHT_DESC_I18N_MAP[desc];
    return key ? t(key) : desc;
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 标签行：只保留分类和 tag，去掉产品代码 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-[#C8714A] font-medium bg-[#C8714A]/10 px-2.5 py-1 rounded-full">
          {translateCategory(category)}
        </span>
        {tags.map((tag) => (
          <span key={tag} className="text-xs text-[#888] bg-[#f5f0eb] px-2.5 py-1 rounded-full">
            {translateTag(tag)}
          </span>
        ))}
      </div>

      {/* 产品名：灰色字体 */}
      <h1 className="text-2xl md:text-3xl font-bold text-[#666] leading-snug">{name}</h1>

      {/* 产品代码：灰色字体，紧贴产品名下方 */}
      {productCode && (
        <p className="text-sm text-[#999] -mt-4">
          {t('detail_product_code')}：{productCode}
        </p>
      )}

      <p className="text-sm text-[#666] leading-relaxed">{description}</p>

      <div className="flex items-center gap-3 py-4 border-y border-[#f0ebe4]">
        <span className="text-3xl font-bold text-[#C8714A]">${price}</span>
        <span className="text-base text-[#ccc] line-through">${originalPrice}</span>
        <span className="px-2.5 py-1 rounded-full bg-[#C8714A] text-white text-xs font-bold">
          {t('detail_save')}${originalPrice - price}
        </span>
        <span className="ml-auto text-xs text-[#aaa] bg-[#f5f0eb] px-2.5 py-1 rounded-full">
          -{discount}% OFF
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {highlights.map((h) => (
          <div key={h.title} className="flex items-start gap-3 p-3 rounded-xl bg-[#faf8f5]">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8714A]/10 flex-shrink-0">
              <i className={`${h.icon} text-[#C8714A] text-sm`}></i>
            </div>
            <div>
              <div className="text-xs font-semibold text-[#1a1a1a]">{translateHighlightTitle(h.title)}</div>
              <div className="text-xs text-[#999] mt-0.5 leading-relaxed">{translateHighlightDesc(h.desc)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Link
          to="/contact"
          className="flex-1 py-3.5 rounded-full bg-[#C8714A] text-white font-semibold text-sm text-center hover:bg-[#b5603b] transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
        >
          <i className="ri-customer-service-line"></i>
          {t('detail_btn_consult')}
        </Link>
        <button className="w-12 h-12 flex items-center justify-center rounded-full border border-[#e8e0d8] hover:border-[#C8714A] hover:text-[#C8714A] transition-colors cursor-pointer text-[#888]">
          <i className="ri-heart-line text-lg"></i>
        </button>
        <button className="w-12 h-12 flex items-center justify-center rounded-full border border-[#e8e0d8] hover:border-[#C8714A] hover:text-[#C8714A] transition-colors cursor-pointer text-[#888]">
          <i className="ri-share-line text-lg"></i>
        </button>
      </div>

      <div className="mt-2">
        <div className="flex gap-1 bg-[#f5f0eb] rounded-full px-1 py-1 w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-[#C8714A] text-white'
                  : 'text-[#666] hover:text-[#C8714A]'
              }`}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>

        <div className="mt-5">
          {activeTab === 'desc' && (
            <div className="text-sm text-[#555] leading-relaxed whitespace-pre-line">
              {longDescription}
            </div>
          )}

          {activeTab === 'specs' && (
            <div className="rounded-xl overflow-hidden border border-[#f0ebe4]">
              {[
                { labelKey: 'detail_spec_material', value: specs.material },
                { labelKey: 'detail_spec_size', value: specs.size },
                { labelKey: 'detail_spec_weight', value: specs.weight },
                ...(specs.moq != null && specs.moq > 0 ? [{ labelKey: 'detail_spec_moq', value: `${specs.moq}` }] : []),
                { labelKey: 'detail_spec_suitable', valueKey: 'detail_spec_suitable_val' },
                { labelKey: 'detail_spec_package', valueKey: 'detail_spec_package_val' },
              ].map((row, i) => (
                <div
                  key={row.labelKey}
                  className={`flex items-center px-4 py-3 text-sm ${
                    i % 2 === 0 ? 'bg-[#faf8f5]' : 'bg-white'
                  }`}
                >
                  <span className="w-28 text-[#999] flex-shrink-0">{t(row.labelKey)}</span>
                  <span className="text-[#1a1a1a] font-medium">
                    {row.value ?? t(row.valueKey as string)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="flex flex-col gap-2">
              {faqs.map((faq, i) => (
                <div key={i} className="rounded-xl border border-[#f0ebe4] overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer hover:bg-[#faf8f5] transition-colors"
                  >
                    <span className="text-sm font-medium text-[#1a1a1a] pr-4">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i
                        className={`ri-arrow-down-s-line text-[#C8714A] transition-transform duration-200 ${
                          openFaq === i ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-[#666] leading-relaxed border-t border-[#f0ebe4] pt-3 bg-[#faf8f5]">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}